/**
 * Timestamps — a point in time: a post's scheduled publish moment, a `created_at`.
 *
 * The wire format is a UTC instant. What a user reads and edits is wall-clock in their profile
 * timezone. Those two conversions are the entire surface; everything else is arithmetic on one
 * side or the other, and Temporal already provides it.
 *
 * dashing-ui's `utils/temporal` covers calendar dates (`PlainDate` ↔ `Date`) but stops short of
 * instants, which is the half the scheduler runs on. This module fills that gap only — it does
 * not re-wrap what dashing-ui already exports.
 *
 * Every function takes the timezone explicitly, the same convention dashing-ui uses. Binding it
 * to the signed-in user is `@/utils/date`'s job, so this module stays free of stores and is
 * testable against any zone without touching the runner's.
 */

/**
 * Parse a wire timestamp.
 *
 * Returns `null` rather than throwing: these strings come off the API and out of URLs, and a
 * caller that has to `try` around a parse tends to skip it. Absent and empty values take the
 * same route — `Temporal.Instant.from` rejects them, so no separate guard earns its place.
 */
export function instantFromIso(iso: string | null | undefined): Temporal.Instant | null {
  try {
    return Temporal.Instant.from(iso as string);
  } catch {
    return null;
  }
}

/** Bridge from a legacy `Date`, for call sites still holding one. */
export function instantFromDate(date: Date): Temporal.Instant | null {
  const ms = date.getTime();
  return Number.isNaN(ms) ? null : Temporal.Instant.fromEpochMilliseconds(ms);
}

/**
 * Parse anything that can name an instant: a wire string, a `Date`, or a value with a numeric
 * `valueOf` such as a Dayjs. Callers holding a value straight off the API or out of a component
 * prop rarely know which of the three they have.
 */
export function instantFrom(value: unknown): Temporal.Instant | null {
  if (typeof value === 'string') return instantFromIso(value);
  // Everything else goes through `Number`, which reads a Date and a Dayjs alike via `valueOf`.
  // `?? NaN` is load-bearing: `Number(null)` is 0, which would make an absent value the epoch.
  try {
    return Temporal.Instant.fromEpochMilliseconds(Number(value ?? NaN));
  } catch {
    return null;
  }
}

/** Wire format. Always UTC, always millisecond precision — what the backend and `Date` agree on. */
export function toIso(instant: Temporal.Instant): string {
  return new Date(instant.epochMilliseconds).toISOString();
}

/** Bridge to a legacy `Date`, for component props that still take one. */
export function toDate(instant: Temporal.Instant): Date {
  return new Date(instant.epochMilliseconds);
}

/** The wall-clock a user in `timezone` reads for this instant. */
export function wallClockIn(instant: Temporal.Instant, timezone: string): Temporal.PlainDateTime {
  return instant.toZonedDateTimeISO(timezone).toPlainDateTime();
}

/**
 * The instant a wall-clock time in `timezone` names — the inverse of {@link wallClockIn}.
 *
 * Uses Temporal's default `'compatible'` disambiguation, which resolves the two times a year a
 * wall-clock is ambiguous the way every other scheduler does: a time skipped by spring-forward
 * moves forward past the gap, and a time repeated by fall-back takes the first (earlier) of the
 * two. Both are lossy by nature; the alternative is throwing at a user who picked 2:30am.
 */
export function instantFromWallClock(
  wallClock: Temporal.PlainDateTime,
  timezone: string,
): Temporal.Instant {
  return wallClock.toZonedDateTime(timezone).toInstant();
}

/** Now, as an instant. Injectable so callers can pass a fixed clock. */
export function nowInstant(): Temporal.Instant {
  return Temporal.Now.instant();
}

/** Today's wall-clock in `timezone`. */
export function nowInZone(timezone: string): Temporal.PlainDateTime {
  return wallClockIn(nowInstant(), timezone);
}
